import * as Sentry from "@sentry/react";
import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import "./index.css";
import "@outpave/ds/fonts.css";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
const graphqlEndpoint = apiBaseUrl
  ? `${apiBaseUrl.replace(/\/$/, "")}/graphql`
  : "/graphql";

const apiHost = (() => {
  try {
    return apiBaseUrl ? new URL(apiBaseUrl).host : null;
  } catch {
    return null;
  }
})();

if (process.env.REACT_APP_SENTRY_ENABLED === "true") {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_SENTRY_ENV,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration(),
      Sentry.feedbackIntegration({
        autoInject: false,
        colorScheme: "light",
        showBranding: false,
        isNameRequired: true,
        isEmailRequired: true,
        useSentryUser: {
          email: "email",
          name: "fullName",
        },
        buttonLabel: "Help",
        formTitle: "Report an Issue?",
        themeLight: {
          accentForeground: "var(--color-text-on-brand)",
          accentBackground: "var(--color-bg-brand)",
          fontFamily: "var(--type-family-sans)",
        },
      }),
      Sentry.graphqlClientIntegration({
        // match your GraphQL endpoint(s)
        endpoints: [graphqlEndpoint, "/graphql"],
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: parseFloat(
      process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATES || "1.0",
    ), //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["localhost", ...(apiHost ? [apiHost] : [])],
    // Session Replay
    replaysSessionSampleRate: parseFloat(
      process.env.REACT_APP_SENTRY_REPLAY_SESSION_SAMPLE_RATES || "0.1",
    ), // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: parseFloat(
      process.env.REACT_APP_SENTRY_ERROR_SAMPLE_RATES || "1.0",
    ), // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement,
);

root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary
      fallback={<p>Something went wrong. Please try again later.</p>}
    >
      <App />
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
